import { useState } from 'react';

const getPageNumberFromUrl = (url) => {
  if (!url) return null;
  const match = url.match(/[?&]page=(\d+)/);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isNaN(parsed) ? null : parsed;
};

const deriveCurrentPage = (data, fallbackPage = 1) => {
  const previousPage = getPageNumberFromUrl(data?.previous);
  if (previousPage) {
    return previousPage + 1;
  }

  const nextPage = getPageNumberFromUrl(data?.next);
  if (nextPage) {
    return nextPage - 1;
  }

  if (data?.currentPage) {
    return data.currentPage;
  }

  return fallbackPage;
};

const usePagination = (fetchFunction, baseUrl, defaultPageSize = 10) => {
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
    pageSize: defaultPageSize,
  });

  const updatePagination = (data, overrides = {}) => {
    setPagination((prev) => {
      const fallbackPage = overrides.currentPage ?? prev.currentPage ?? 1;
      const pageSizeOverride =
        overrides.pageSize || prev.pageSize || defaultPageSize;

      return {
        count: data?.count ?? prev.count ?? 0,
        next: data?.next ?? null,
        previous: data?.previous ?? null,
        currentPage: overrides.currentPage
          ?? deriveCurrentPage(data, fallbackPage),
        pageSize: pageSizeOverride,
      };
    });
  };

  const fetchNext = async () => {
    if (pagination.next) {
      await fetchFunction({ url: pagination.next });
    }
  };

  const fetchPrevious = async () => {
    if (pagination.previous) {
      await fetchFunction({ url: pagination.previous });
    }
  };

  const goToPage = async (pageNumber) => {
    const totalPages = Math.max(
      1,
      Math.ceil(pagination.count / (pagination.pageSize || defaultPageSize))
    );

    const targetPage = Math.min(
      Math.max(pageNumber, 1),
      totalPages,
    );

    if (targetPage === pagination.currentPage) {
      return;
    }

    await fetchFunction({ url: baseUrl, page: targetPage });
  };

  return { pagination, updatePagination, fetchNext, fetchPrevious, goToPage };
};

export default usePagination;
